import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { observer } from 'mobx-react';
import { NavLink } from 'react-router-dom';
import { useStore } from '../../../../../../stores/root';

// assets
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faHome, faXmarkCircle } from '@fortawesome/pro-light-svg-icons';

// services
import GeneralService from '../../../../../../services/general';
import EmployeeTargetSummaryService, { ITargetSummaryProps } from '../../../../../../services/employees/targetSummary';

// props
import { IEmployeeContractResourceProps } from '../../../../../../props/employees/contracts/contract';

// components
import { ActionButton, CommandBar, IButtonProps, ICommandBarItemProps, PrimaryButton, ProgressIndicator, Stack } from '@fluentui/react';
import Text from '../../../../../typography/text';
import LoadingComponent from '../../../../../feedbacks/loading';
import Label from '../../../../../typography/label';
import Details from '../../../../../typography/details';
import Tag from '../../../../../uiframeworks/tag';

interface EmployeeContractDetailsPropertiesProps {
    contract: IEmployeeContractResourceProps;
}

const EmployeeContractDetailsProperties: React.FC<EmployeeContractDetailsPropertiesProps> = observer((props: EmployeeContractDetailsPropertiesProps) => {
    const { banner, topbar, user } = useStore();
    const { contract } = props;
    const [targetSummary, setTargetSummary] = useState<ITargetSummaryProps | undefined>();
    const [loadingTarget, setLoadingTarget] = useState<boolean>(false);

    useEffect(() => {
        if (contract?.employee?.id && Number(contract.targetAmount || 0) > 0) {
            setLoadingTarget(true);
            EmployeeTargetSummaryService.get(contract.employee.id)
                .then((res) => {
                    setTargetSummary(res);
                    setLoadingTarget(false);
                })
                .catch(() => {
                    setLoadingTarget(false);
                });
        }
    }, [contract?.employee?.id, contract?.targetAmount]);

    const percentage = Number(targetSummary?.percentage ?? 0);
    const achievedAmount = Number(targetSummary?.achievedAmount ?? 0);
    const remainingAmount = Number(targetSummary?.remainingAmount ?? 0);
    const ordersCount = Number(targetSummary?.ordersCount ?? 0);
    const isTargetMet = Boolean(targetSummary?.isTargetMet);

    return <Stack tokens={{ childrenGap: 20 }}>
        <Stack tokens={{ childrenGap: 20 }} horizontal>
            <Stack styles={{ root: { width: '50%' } }}>
                <Details title={'Contract number'} text={'#' + contract.id} />
            </Stack>
            <Stack styles={{ root: { width: '50%' } }}> </Stack>
        </Stack>
        <Stack tokens={{ childrenGap: 20 }} horizontal>
            <Stack styles={{ root: { width: '50%' } }}>
                <Details title={'Employee'} text={contract.employee.name} />
            </Stack>
            <Stack styles={{ root: { width: '50%' } }}>
                <Details title={'Subsidiary'} text={contract.subsidiary.name} />
            </Stack>
        </Stack>
        <Stack tokens={{ childrenGap: 20 }} horizontal>
            <Stack styles={{ root: { width: '50%' } }}>
                <Details title={'Start date'} text={moment(contract.startDate).format("DD/MM/YYYY")} />
            </Stack>
            <Stack styles={{ root: { width: '50%' } }}>
                <Details title={'End date'} text={moment(contract.endDate).format("DD/MM/YYYY")} />
            </Stack>
        </Stack>
        <Stack className={'divider'}></Stack>
        <Stack tokens={{ childrenGap: 20 }} horizontal>
            <Stack styles={{ root: { width: '50%' } }}>
                <Details title={'Base salary'} text={`Rp. ${GeneralService.getNumberWithSeparator(Number(contract.salary))}`} />
            </Stack>
            <Stack styles={{ root: { width: '50%' } }}>
                <Details title={`Monthly target (${contract.targetType === 'profit' ? 'Profit' : 'Omset'})`} text={`Rp. ${GeneralService.getNumberWithSeparator(Number(contract.targetAmount || 0))}`} />
            </Stack>
        </Stack>

        {Number(contract.targetAmount || 0) > 0 ? (
            <Stack tokens={{ childrenGap: 10 }} style={{
                padding: 16,
                borderRadius: 8,
                backgroundColor: '#faf9f8',
                border: '1px solid #edebe9'
            }}>
                <Stack horizontal horizontalAlign={'space-between'} verticalAlign={'center'}>
                    <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign={'center'}>
                        <Label size={'small'}>Pencapaian Target Bulan Ini</Label>
                        <Tag size={'small'} text={`Basis: ${contract.targetType === 'profit' ? 'Profit' : 'Omset'}`} />
                    </Stack>
                    <Label size={'medium'} style={{ color: percentage >= 100 ? '#107c41' : '#0078d4', fontWeight: 600 }}>
                        {`${percentage}% Achieved`}
                    </Label>
                </Stack>

                <ProgressIndicator
                    percentComplete={Math.min(1, percentage / 100)}
                    barHeight={8}
                />

                <Stack horizontal horizontalAlign={'space-between'} verticalAlign={'center'}>
                    <Stack tokens={{ childrenGap: 2 }}>
                        <Text size={'xsmall'}>Realisasi ({contract.targetType === 'profit' ? 'Nett Profit' : 'Omset'})</Text>
                        <Label size={'small'}>Rp. {GeneralService.getNumberWithSeparator(achievedAmount)}</Label>
                    </Stack>
                    <Stack tokens={{ childrenGap: 2 }} horizontalAlign={'end'}>
                        <Text size={'xsmall'}>Target Kontrak</Text>
                        <Label size={'small'}>Rp. {GeneralService.getNumberWithSeparator(Number(contract.targetAmount || 0))}</Label>
                    </Stack>
                </Stack>

                {targetSummary ? (
                    <Stack horizontal horizontalAlign={'space-between'} verticalAlign={'center'} style={{ marginTop: 4 }}>
                        <Text size={'xsmall'}>Total Order: {ordersCount}</Text>
                        {isTargetMet ? (
                            <Label size={'xsmall'} style={{ color: '#107c41', fontWeight: 600 }}>Target Met! 🎉</Label>
                        ) : (
                            <Text size={'xsmall'} style={{ color: '#605e5c' }}>
                                Sisa Target: <strong style={{ color: '#323130' }}>Rp. {GeneralService.getNumberWithSeparator(remainingAmount)}</strong>
                            </Text>
                        )}
                    </Stack>
                ) : null}
            </Stack>
        ) : null}

        <Stack className={'divider'}></Stack>
        <Details title={'Jobdesks'}>
            <Stack tokens={{ childrenGap: 5 }} horizontal wrap styles={{ root: { position: 'relative', top: 3 } }}>
                {(contract.jobdesks || []).map((jobdesk, index) => <Tag key={`jobdesk_${index}`} size={'small'} text={jobdesk.name} />)}
            </Stack>
        </Details>
    </Stack>;
});

export default EmployeeContractDetailsProperties;
